import React, { useState, useEffect } from 'react';
import AuthorFields from '../components/AuthorFields';
import CitationResult from '../components/CitationResult';
import { formatAuthorsForCitation, formatAuthorsForParenthetical, formatAuthorsForReference, formatURL } from '../utils/citationHelpers';

function NewspaperForm() {
  const [authors, setAuthors] = useState([]);
  const [authorType, setAuthorType] = useState('personal');
  const [hasAuthors, setHasAuthors] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    newspaper: '',
    year: '',
    day: '',
    month: '',
    section: '',
    pages: '',
    url: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleHasAuthorsChange = (e) => {
    setHasAuthors(e.target.checked);
    if (!e.target.checked) {
      setAuthors([]);
    }
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    if (formData.title && formData.newspaper && formData.year) {
      if (hasAuthors && authors.length > 0) {
        generatePreview();
      } else if (!hasAuthors) {
        generatePreviewNoAuthor();
      }
    } else {
      setLiveResult(null);
    }
  }, [authors, formData, hasAuthors]);

  const formatDate = () => {
    const { year, month, day } = formData;
    if (!year) return '';

    if (day && month) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `${year}, ${parseInt(day)} de ${monthNames[month]}`;
    } else if (month) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `${year}, ${monthNames[month]}`;
    }
    return year;
  };

  // Función para extraer las primeras 3-4 palabras significativas del título
  const extractShortTitle = (title) => {
    if (!title) return '';
    const articles = ['la', 'las', 'el', 'los', 'le', 'les', 'the', 'a', 'an', 'un', 'una', 'unos', 'unas', 'de', 'del', 'en', 'con', 'por', 'para', 'sin', 'sobre', 'bajo'];
    const words = title.toLowerCase().split(/\s+/);
    let significantWords = [];
    let startIndex = 0;

    // Saltar artículos y preposiciones al inicio
    while (startIndex < words.length && articles.includes(words[startIndex])) {
      startIndex++;
    }

    // Tomar hasta 4 palabras significativas
    for (let i = startIndex; i < words.length && significantWords.length < 4; i++) {
      if (!articles.includes(words[i]) || (significantWords.length > 0 && significantWords.length < 3)) {
        significantWords.push(words[i]);
      }
    }

    // Capitalizar la primera palabra
    if (significantWords.length > 0) {
      significantWords[0] = significantWords[0].charAt(0).toUpperCase() + significantWords[0].slice(1);
    }

    return significantWords.join(' ');
  };

  // Función para formatear el título como autor en la referencia
  const formatTitleAsAuthor = (title) => {
    if (!title) return '';
    const articles = ['la', 'las', 'el', 'los', 'le', 'les', 'the', 'a', 'an', 'un', 'una', 'unos', 'unas', 'de', 'del', 'en', 'con', 'por', 'para', 'sin', 'sobre', 'bajo'];
    const words = title.split(/\s+/);
    const firstWordLower = words[0].toLowerCase();

    if (articles.includes(firstWordLower)) {
      // Si comienza con artículo o preposición
      const firstWord = firstWordLower.charAt(0).toUpperCase() + firstWordLower.slice(1);
      const restOfTitle = words.slice(1).join(' ');
      // Asegurar que la primera letra del título (ahora sin el artículo) esté en mayúscula
      const capitalizedRest = restOfTitle.charAt(0).toUpperCase() + restOfTitle.slice(1);
      return `${capitalizedRest}, ${firstWord}`;
    } else {
      // Si no comienza con artículo o preposición, simplemente asegurar que la primera letra esté en mayúscula
      return title.charAt(0).toUpperCase() + title.slice(1);
    }
  };

  const generatePreviewNoAuthor = () => {
    const shortTitle = extractShortTitle(formData.title);
    
    const citation = {
      narrative: null, // No existe cita narrativa cuando el título es el autor
      parenthetical: `("${shortTitle}", ${formData.year})`
    };

    const titleAsAuthor = formatTitleAsAuthor(formData.title);
    const dateFormatted = formatDate();
    const formattedUrl = formatURL(formData.url);

    let referenceText = `${titleAsAuthor}. (${dateFormatted}). <em>${formData.newspaper}</em>`;
    
    if (formData.section) {
      referenceText += `, ${formData.section}`;
    }
    
    if (formData.pages) {
      referenceText += `, ${formData.pages}`;
    }
    
    if (formattedUrl) {
      referenceText += `. ${formattedUrl}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generatePreview = () => {
    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const dateFormatted = formatDate();
    const formattedUrl = formatURL(formData.url);

    let referenceText = `${authorsForReference} (${dateFormatted}). ${formData.title}. <em>${formData.newspaper}</em>`;
    
    if (formData.section) {
      referenceText += `, ${formData.section}`;
    }
    
    if (formData.pages) {
      referenceText += `, ${formData.pages}`;
    }
    
    if (formattedUrl) {
      referenceText += `. ${formattedUrl}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    if (!formData.title || !formData.newspaper || !formData.year) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, completa todos los campos obligatorios.');
      return;
    }

    if (hasAuthors && authors.length === 0) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, agrega al menos un autor o marca la opción "Sin autor".');
      return;
    }

    if (!hasAuthors) {
      const shortTitle = extractShortTitle(formData.title);
      
      const citation = {
        narrative: null, // No existe cita narrativa cuando el título es el autor
        parenthetical: `("${shortTitle}", ${formData.year})`
      };

      const titleAsAuthor = formatTitleAsAuthor(formData.title);
      const dateFormatted = formatDate();
      const formattedUrl = formatURL(formData.url);

      let referenceText = `${titleAsAuthor}. (${dateFormatted}). <em>${formData.newspaper}</em>`;
      
      if (formData.section) {
        referenceText += `, ${formData.section}`;
      }
      
      if (formData.pages) {
        referenceText += `, ${formData.pages}`;
      }
      
      if (formattedUrl) {
        referenceText += `. ${formattedUrl}`;
      } else {
        referenceText += '.';
      }

      const reference = {
        html: referenceText,
        text: referenceText.replace(/<[^>]*>/g, '')
      };

      setResult({ citation, reference });
      return;
    }

    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const dateFormatted = formatDate();
    const formattedUrl = formatURL(formData.url);

    let referenceText = `${authorsForReference} (${dateFormatted}). ${formData.title}. <em>${formData.newspaper}</em>`;
    
    if (formData.section) {
      referenceText += `, ${formData.section}`;
    }
    
    if (formData.pages) {
      referenceText += `, ${formData.pages}`;
    }
    
    if (formattedUrl) {
      referenceText += `. ${formattedUrl}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Artículo de periódico</h1>
        <p className="text-gray-600 dark:text-gray-400">Artículos de prensa</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 dark:bg-gray-800 dark:border-gray-700">
          <div>
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={hasAuthors}
                onChange={handleHasAuthorsChange}
                className="mr-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                El artículo tiene autor(es)
              </span>
            </label>
            {!hasAuthors && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 dark:bg-yellow-900/20 dark:border-yellow-800/50">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Sin autor:</strong> El título del artículo será usado como autor en la referencia. Solo se generará cita parentética.
                </p>
              </div>
            )}
          </div>

          {hasAuthors && (
            <AuthorFields
              authors={authors}
              setAuthors={setAuthors}
              authorType={authorType}
              setAuthorType={setAuthorType}
            />
          )}

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Título del artículo *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label htmlFor="newspaper" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Nombre del periódico *
            </label>
            <input
              type="text"
              id="newspaper"
              name="newspaper"
              value={formData.newspaper}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Año *
              </label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            <div>
              <label htmlFor="day" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Día
              </label>
              <input
                type="number"
                id="day"
                name="day"
                value={formData.day}
                onChange={handleInputChange}
                min="1"
                max="31"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Mes
              </label>
              <select
                id="month"
                name="month"
                value={formData.month}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">Seleccionar</option>
                <option value="01">Enero</option>
                <option value="02">Febrero</option>
                <option value="03">Marzo</option>
                <option value="04">Abril</option>
                <option value="05">Mayo</option>
                <option value="06">Junio</option>
                <option value="07">Julio</option>
                <option value="08">Agosto</option>
                <option value="09">Septiembre</option>
                <option value="10">Octubre</option>
                <option value="11">Noviembre</option>
                <option value="12">Diciembre</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="section" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Sección
              </label>
              <input
                type="text"
                id="section"
                name="section"
                value={formData.section}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="ej: Economía, Cultura, etc."
              />
            </div>
            <div>
              <label htmlFor="pages" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Páginas (ej: A1, 15-16)
              </label>
              <input
                type="text"
                id="pages"
                name="pages"
                value={formData.pages}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="A1, 15-16"
              />
            </div>
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              URL
            </label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>

          <button
            type="button"
            onClick={generateCitation}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generar cita y referencia
          </button>
        </div>

        <div className="space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}
          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default NewspaperForm;